import { Fragment, type PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useViewport } from '@/hooks';
import { useUser } from '@/lib/auth';
import { useUserPlanManager } from '@/shared/plan/UserPlanManagerProvider';
import { MapTemplateTypeLocaleKey } from '@/types';
import { type MapTemplate, type MapTemplateType, MapTemplateTypes } from '@/types/MapTemplate';
import { type CommonTranslationKeys } from '@/types/translation';
import { cn } from '@/utils/tailwind.util';
import { SECTION_KEYS, type SectionKey } from '../constants';
import { useOrganizeMaps } from '../hooks';
import { MapTemplateCard } from './MapTemplateCard';
import { MapTemplateDetail } from './MapTemplateDetail';
import { MapTemplateSubmitAction } from './MapTemplateSubmitAction';
import { MapTemplateTypeSideBar } from './MapTemplateTypeSideBar';
import { SectionNavBar } from './SectionNavBar';
interface MapSelectionViewProps {
  /** 사용 가능한 맵 템플릿 목록 */
  mapTemplates: MapTemplate[];
  /** 현재 선택된 맵 템플릿 ID (선택사항) */
  selectedMapTemplateId?: string;
  /** 유저 국가 코드 — KR인 경우 일부 템플릿 노출 제한에 사용 */
  userCountryCode?: string;
  isChange?: boolean;
  focusFirstFreeMap?: boolean;
  /** 맵 선택 완료 시 호출되는 콜백 (선택된 맵 ID와 타입 전달) */
  onComplete: (selectedMapTemplateId: string, mapTemplateType: MapTemplateType) => void;
}

/**
 * MapSelectionView - 독립적인 맵 선택 뷰 컴포넌트
 *
 * 이 컴포넌트는 맵 템플릿 선택 인터페이스를 독립된 뷰로 제공합니다.
 * 어떤 컨테이너(모달, 페이지 등)에도 임베드될 수 있으며 props를 통해 통신합니다.
 *
 * @param mapTemplates - 사용 가능한 맵 템플릿 배열
 * @param selectedMapTemplateId - 현재 선택된 맵 템플릿 ID (선택사항)
 * @param onComplete - 맵 선택 완료 시 호출되는 콜백, 선택된 맵 ID와 타입을 전달
 */
export const MapSelectionView = ({
  mapTemplates,
  selectedMapTemplateId,
  isChange = false,
  focusFirstFreeMap = false,
  onComplete
}: MapSelectionViewProps) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    user
  } = useUser();
  const {
    userPlanManager
  } = useUserPlanManager();
  const {
    isMobile,
    isTabletSm
  } = useViewport();
  const isMobileView = isMobile || isTabletSm;
  const SECTION_TITLES: Record<SectionKey, string> = {
    NEW: t('build:mapTemplate.recommendedMaps'),
    FREE: t('build:mapTemplate.freeMaps'),
    BASIC: t('build:mapTemplate.basicMaps'),
    PRO: t('build:mapTemplate.proMaps')
  };
  const [activeTab, setActiveTab] = useState<MapTemplateType>('total');

  // 협동전 탭은 서버가 협동전 템플릿을 내려줄 때만 노출한다(템플릿 없는 빈 탭 방지).
  const templateTypes = MapTemplateTypes.filter(type => type !== 'coop' || mapTemplates.some(map => map.type === 'coop'));
  const {
    mapsInTab,
    sectionKeys,
    groupedMapsInTab
  } = useOrganizeMaps({
    maps: mapTemplates,
    tab: activeTab,
    user: user
  });

  // sectionKeys 순서대로 사용자가 접근 가능한 첫 번째 맵 찾기
  const findFirstAccessibleMap = () => {
    for (const sectionKey of sectionKeys) {
      const mapsInSection = groupedMapsInTab[sectionKey as SectionKey];
      const accessibleMap = mapsInSection.find(map => userPlanManager.hasEnoughPlanType(map.templatePlanType));
      if (accessibleMap) return accessibleMap;
    }
    return groupedMapsInTab[sectionKeys[0] as SectionKey][0];
  };
  const [selectedMap, setSelectedMap] = useState<MapTemplate>(() => {
    if (focusFirstFreeMap) {
      // 화면에 첫 번째로 보이는 FREE 맵과 동일한 맵을 선택하기 위해 섹션 그룹 순서를 사용한다.
      const firstFreeMap = groupedMapsInTab['FREE']?.[0];
      if (firstFreeMap) return firstFreeMap;
    }
    return mapTemplates.find(map => map.templateId === selectedMapTemplateId) ?? findFirstAccessibleMap();
  });
  const [shouldScrollToSelectedMap, setShouldScrollToSelectedMap] = useState(true);
  const handleSubmit = (map: MapTemplate) => {
    onComplete(map.templateId, map.type);
  };

  // 맵 엘리먼트로 스크롤
  const scrollToMap = (map: MapTemplate) => {
    document.getElementById(`map-${map.templateId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  // 탭 전환 시, 이전 탭에서 선택된 맵이 새로운 탭에 존재하지 않는다면 선택된 맵을 변경한다. 이때 해당 맵은 유저가 쓸 수 있는 맵에서 가장 최상단 맵이다.
  // 단, 외부에서 선택된 템플릿(selectedMapTemplateId)은 우측 미리보기 유지를 위해 리셋하지 않는다.
  useEffect(() => {
    if (selectedMap.templateId === selectedMapTemplateId) return;
    const isSelectedMapInCurrentTab = mapsInTab.some(map => map.templateId === selectedMap.templateId);
    if (!isSelectedMapInCurrentTab) {
      setSelectedMap(findFirstAccessibleMap());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsInTab]);
  useEffect(() => {
    setShouldScrollToSelectedMap(true);
  }, [activeTab]);
  useEffect(() => {
    if (shouldScrollToSelectedMap) {
      const timeout = setTimeout(() => {
        scrollToMap(selectedMap);
        setShouldScrollToSelectedMap(false);
      });
      return () => clearTimeout(timeout);
    }
  }, [shouldScrollToSelectedMap, selectedMap]);
  return <main className="flex min-h-0 w-full flex-1 bg-neutral" data-sentry-component="MapSelectionView" data-sentry-source-file="MapSelectionView.tsx">
      <div className="flex min-h-0 w-full flex-col gap-md p-md pb-0 tablet-sm:flex-row tablet-sm:pl-lg tablet-sm:pr-3xs">
        {/* 왼쪽 사이드바 - 고정 */}
        <MapTemplateTypeSideBar activeTab={activeTab} templateTypes={templateTypes} onTabChange={setActiveTab} data-sentry-element="MapTemplateTypeSideBar" data-sentry-source-file="MapSelectionView.tsx" />

        {/* 중간 섹션 - 헤더 고정, 리스트만 스크롤 */}
        <div className="flex min-h-0 w-full flex-col gap-md tablet-sm:gap-xs tablet-sm:pr-md">
          {/* 고정 헤더 영역 */}
          {activeTab !== 'total' && <TabTitles tab={activeTab} />}
          <SectionNavBar sectionKeys={(activeTab === 'total' ? SECTION_KEYS : SECTION_KEYS.filter(key => key !== 'NEW')) as SectionKey[]} data-sentry-element="SectionNavBar" data-sentry-source-file="MapSelectionView.tsx" />

          {/* 스크롤 영역 - 맵 리스트만 */}
          <div className="flex-1 overflow-y-auto pb-[144px] tablet-sm:pb-3xl">
            <div className="flex flex-col gap-xs">
              {sectionKeys.map((sectionKey, i) => {
              const maps = groupedMapsInTab[sectionKey];
              return <Fragment key={sectionKey}>
                    <SectionTitle id={`section-${sectionKey}`} className={cn(i !== 0 && 'pt-sm')}>
                      {SECTION_TITLES[sectionKey]}
                    </SectionTitle>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-xs">
                      {maps.map(map => <MapTemplateCard key={map.templateId} map={map} showMapType={activeTab === 'total'} isSelected={map.templateId === selectedMap.templateId} onClick={() => setSelectedMap(map)} />)}
                    </div>
                  </Fragment>;
            })}
            </div>
          </div>
        </div>
      </div>

      {isMobileView ? <MapTemplateSubmitAction className="fixed bottom-0 z-[30] w-full bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFF_10%,#FFF_100%)] p-md" map={selectedMap} isChange={isChange} onSubmit={handleSubmit} /> :
    // 미리보기 섹션 - 고정
    <MapTemplateDetail map={selectedMap} isChange={isChange} onSubmit={handleSubmit} />}
    </main>;
};
const TabTitles = ({
  tab
}: {
  tab: MapTemplateType;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <div className="flex flex-col gap-2xs tablet-sm:pb-xs" data-sentry-component="TabTitles" data-sentry-source-file="MapSelectionView.tsx">
      <h1 className="text-heading-lg-extrabold">
        {t(MapTemplateTypeLocaleKey[tab])}
      </h1>
      <p className="text-body-sm-medium text-neutral">
        {t(`common:map.${tab}.description` as CommonTranslationKeys)}
      </p>
    </div>;
};
const SectionTitle = ({
  id,
  className,
  children
}: PropsWithChildren<{
  id?: string;
  className?: string;
}>) => <h4 id={id} className={cn('text-body-sm-semibold text-alternative', className)} style={{
  scrollMarginTop: '44px'
}} data-sentry-component="SectionTitle" data-sentry-source-file="MapSelectionView.tsx">
    {children}
  </h4>;